import { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Volume2 } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import { Zone } from '@/core/api/entities/zone/zone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Label } from '@/shared/components/atoms/Label/Label'
import { Slider } from '@/shared/components/atoms/Slider'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface ManageZoneVolumeModalProps {
	visible: boolean
	zone: Zone | null
	onClose: () => void
	onConfirm: (data: {
		box_volume: number
		is_smart_volume_enabled: boolean
	}) => void
	isUpdating: boolean
}

interface VolumeFormData {
	volume: number
}

export const ManageZoneVolumeModal = ({
	visible,
	zone,
	onClose,
	onConfirm,
	isUpdating,
}: ManageZoneVolumeModalProps) => {
	const { t } = useTranslation('zones')

	const getVolumeValue = (z: Zone | null): number => {
		if (!z) return 3
		if (z.boxVolume !== null && z.boxVolume !== undefined) {
			return Math.min(Math.max(z.boxVolume, 0), 7)
		}
		return 3
	}

	const schema = Yup.object().shape({
		volume: Yup.number().required().min(0).max(7).integer(),
	})

	const { control, handleSubmit, reset } = useForm<VolumeFormData>({
		resolver: yupResolver(schema),
		defaultValues: { volume: getVolumeValue(zone) },
	})

	useEffect(() => {
		if (zone) {
			reset({ volume: getVolumeValue(zone) })
		}
	}, [zone, reset])

	const onSubmit = (data: VolumeFormData) => {
		onConfirm({
			box_volume: data.volume,
			is_smart_volume_enabled: zone?.isSmartVolumeEnabled ?? false,
		})
	}

	const handleClose = () => {
		reset()
		onClose()
	}

	return (
		<Dialog open={visible} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-[700px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				<div className="px-6 py-6 border-b flex items-center justify-between">
					<DialogTitle className="text-2xl">
						{t('location.monitor.volumeModal.title')}
					</DialogTitle>
				</div>

				<form
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col flex-1 min-h-0"
				>
					<div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
						<p className="text-sm text-muted-foreground">
							{t('location.monitor.volumeModal.description')}
						</p>

						<div className="space-y-2">
							<Label className="text-sm font-medium text-muted-foreground">
								{t('location.monitor.volumeModal.sliderLabel')}
							</Label>
							<Controller
								name="volume"
								control={control}
								render={({ field }) => (
									<div className="flex border rounded-lg p-3 items-center justify-between gap-3 bg-background">
										<Volume2 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
										<Slider
											value={[field.value]}
											onValueChange={(value) => field.onChange(value[0])}
											max={7}
											min={0}
											step={1}
											className="flex-1"
											disabled={isUpdating}
										/>
										<span className="text-sm font-semibold w-8 text-right flex-shrink-0">
											{field.value}
										</span>
									</div>
								)}
							/>
						</div>

						<div className="space-y-2">
							<p className="text-lg font-semibold">
								{t('location.monitor.volumeModal.smartTitle')}
							</p>
						</div>
					</div>

					<div className="px-6 py-6 border-t flex justify-end gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={handleClose}
							disabled={isUpdating}
							className="rounded-full px-6"
						>
							{t('location.monitor.volumeModal.close')}
						</Button>
						<Button
							type="submit"
							variant="default"
							disabled={isUpdating}
							className="rounded-full px-6"
						>
							{t('location.monitor.volumeModal.save')}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	)
}
